import {
  RPC_TOOLS_PATH,
  deriveDispatchBoard,
  type DispatchRawBoard,
  type RpcOk,
  type RpcResponse,
  type RpcToolsResponse,
} from '@phinest/shared';
import { appConfig } from './config';
import { accessToken } from './supabase';
import { supabase } from './supabase';

/**
 * Database-only admin operations that bypass the Cloudflare API and execute as
 * one PostgREST RPC transaction. Keep external effects (mail, AI, OAuth,
 * banking sync), reports and confirmation-gated financial operations on the
 * Worker until they have an equivalent database command.
 */
export const DIRECT_ADMIN_TOOLS = new Set([
  'get_workspace_overview',
  'search_customers',
  'get_customer',
  'get_customer_hub',
  'issue_customer_hub',
  'revoke_customer_hub',
  'resolve_customer_service_request',
  'get_customer_hub_settings',
  'create_customer',
  'update_customer',
  'add_contact',
  'add_service_location',
  'update_service_location',
  'list_services',
  'list_jobs',
  'get_job',
  'create_job',
  'update_job',
  'add_job_lines',
  'complete_job',
  'list_appointments',
  'list_staff',
  'schedule_appointment',
  'update_appointment',
  'get_dispatch_board',
  'get_appointment_communication',
  'list_appointment_communication_attention',
  'get_appointment_communication_settings',
  'list_service_plans',
  'get_service_plan',
  'preview_service_plan',
  'list_intake_forms',
  'list_intake_submissions',
  'get_intake_submission',
  'list_lead_response_attention',
  'get_lead_response',
  'get_lead_response_settings',
  'record_lead_contact',
  'schedule_lead_follow_up',
  'list_quotes',
  'get_quote',
  'create_quote',
  'update_quote',
  'set_quote_status',
  'set_quote_options',
  'preview_quote_selection',
  'set_quote_deposit',
  'preview_quote_deposit',
  'record_quote_deposit',
  'list_job_change_orders',
  'get_change_order',
  'preview_change_order',
  'create_change_order',
  'update_change_order',
  'cancel_change_order',
  'get_change_order_settings',
  'list_invoices',
  'get_invoice',
  'get_invoice_collection',
  'list_invoice_collection_attention',
  'get_invoice_reminder_settings',
  'preview_invoice_reminder',
  'get_unbilled_work_review',
  'list_unbilled_work_attention',
  'get_unbilled_work_settings',
  'list_operator_handoffs',
  'get_operator_handoff',
  'set_operator_handoff_snooze',
  'get_operator_desk_settings',
  'list_bank_connections',
  'list_bills',
  'get_bill',
  'get_bill_settings',
  'create_bill',
  'update_bill',
  'list_documents',
  'get_document',
  'create_document',
]);

/** A structured error from the tool surface, carrying the agent-readable hint. */
export class RpcError extends Error {
  readonly code: string;
  readonly hint?: string;
  readonly details?: Record<string, unknown>;
  readonly status: number;
  constructor(
    status: number,
    body: { error: string; message: string; hint?: string; details?: Record<string, unknown> },
  ) {
    super(body.message);
    this.name = 'RpcError';
    this.status = status;
    this.code = body.error;
    this.hint = body.hint;
    this.details = body.details;
  }
  /** Message plus hint, for a toast. */
  get full(): string {
    return this.hint ? `${this.message} ${this.hint}` : this.message;
  }
}

let onUnauthorized: (() => void) | undefined;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export interface CallOptions {
  dryRun?: boolean;
  confirmToken?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const token = await accessToken();
  if (!token) {
    onUnauthorized?.();
    throw new RpcError(401, {
      error: 'unauthenticated',
      message: 'Your session has expired.',
      hint: 'Sign in again.',
    });
  }
  let res: Response;
  try {
    res = await fetch(`${appConfig.apiUrl}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new RpcError(0, {
      error: 'internal',
      message: 'Could not reach the server.',
      hint: `Check that the API at ${appConfig.apiUrl} is running. (${(e as Error).message})`,
    });
  }
  if (res.status === 401) onUnauthorized?.();
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    throw new RpcError(res.status, {
      error: 'internal',
      message: `The server returned a non-JSON response (${res.status}).`,
      details: { body: text.slice(0, 400) },
    });
  }
  const envelope = body as RpcResponse<T> & {
    error?: { error: string; message: string; hint?: string; details?: Record<string, unknown> };
  };
  if (!res.ok || envelope.ok === false) {
    const err = envelope.error ?? { error: 'internal', message: `Request failed (${res.status}).` };
    throw new RpcError(res.status, err);
  }
  return envelope as unknown as T;
}

interface DirectEnvelope<T> {
  summary?: string;
  data: T;
}

function directStatus(code?: string): number {
  if (code === '42501' || code === 'PGRST301') return 403;
  if (code === 'P0002') return 404;
  if (
    code === '22023' ||
    code === '23502' ||
    code === '23503' ||
    code === '23505' ||
    code === '23514'
  )
    return 400;
  return 500;
}

function directUnavailable(code?: string): boolean {
  // Rolling deploy safety: an older database may briefly serve a newer SPA.
  // Do not include PostgreSQL 42883: it can also identify an undefined operator
  // inside the function, which must surface as an implementation error.
  return code === 'PGRST202';
}

async function callDirect<T>(
  tool: string,
  args: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<RpcOk<T> | null> {
  const changeOrderAction =
    tool === 'list_job_change_orders'
      ? 'list'
      : tool === 'get_change_order'
        ? 'get'
        : tool === 'preview_change_order'
          ? 'preview'
          : tool === 'create_change_order'
            ? 'create'
            : tool === 'update_change_order'
              ? 'update'
              : tool === 'cancel_change_order'
                ? 'cancel'
                : tool === 'get_change_order_settings'
                  ? 'get_settings'
                  : null;
  if (changeOrderAction) {
    const rpcArgs = tool === 'list_job_change_orders' ? { job_id: args.job_id } : args;
    let query = supabase.rpc('change_order_rpc', { p_action: changeOrderAction, p_args: rpcArgs });
    if (signal) query = query.abortSignal(signal);
    const { data, error } = await query;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Direct change-order command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  const quoteChoiceAction =
    tool === 'set_quote_options' ? 'set' : tool === 'preview_quote_selection' ? 'preview' : null;
  if (quoteChoiceAction) {
    let choiceQuery = supabase.rpc('quote_choices_rpc', {
      p_action: quoteChoiceAction,
      p_args: args,
    });
    if (signal) choiceQuery = choiceQuery.abortSignal(signal);
    const { data, error } = await choiceQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Direct quote-choice command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  const quoteDepositAction =
    tool === 'set_quote_deposit'
      ? 'set'
      : tool === 'preview_quote_deposit'
        ? 'preview'
        : tool === 'record_quote_deposit'
          ? 'record'
          : null;
  if (quoteDepositAction) {
    let depositQuery = supabase.rpc('quote_deposit_rpc', {
      p_action: quoteDepositAction,
      p_args: args,
    });
    if (signal) depositQuery = depositQuery.abortSignal(signal);
    const { data, error } = await depositQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Direct quote-deposit command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  if (tool === 'evaluate_lifecycle_transition') {
    let policyQuery = supabase.rpc('lifecycle_policy_evaluate', {
      p_entity: args.entity as string,
      p_entity_id: args.entity_id as string,
      p_to_status: args.to_status as string,
    });
    if (signal) policyQuery = policyQuery.abortSignal(signal);
    const { data, error } = await policyQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    return { ok: true, summary: '', data: data as T };
  }

  if (tool === 'get_dispatch_board') {
    let dispatchQuery = supabase.rpc('dispatch_rpc', {
      p_action: 'board',
      p_args: args,
    });
    if (signal) dispatchQuery = dispatchQuery.abortSignal(signal);
    const { data, error } = await dispatchQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<DispatchRawBoard> | null;
    if (!envelope?.data) {
      throw new RpcError(500, {
        error: 'internal',
        message: 'Direct dispatch command returned no data.',
      });
    }
    return {
      ok: true,
      summary: envelope.summary ?? '',
      data: deriveDispatchBoard(envelope.data) as T,
    };
  }

  const appointmentAction =
    tool === 'get_appointment_communication'
      ? 'detail'
      : tool === 'list_appointment_communication_attention'
        ? 'attention'
        : tool === 'get_appointment_communication_settings'
          ? 'settings'
          : null;
  if (appointmentAction) {
    let communicationQuery = supabase.rpc('appointment_communication_rpc', {
      p_action: appointmentAction,
      p_args: args,
    });
    if (signal) communicationQuery = communicationQuery.abortSignal(signal);
    const { data, error } = await communicationQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Appointment communication command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  const invoiceReminderAction =
    tool === 'get_invoice_collection'
      ? 'detail'
      : tool === 'list_invoice_collection_attention'
        ? 'attention'
        : tool === 'get_invoice_reminder_settings'
          ? 'settings'
          : tool === 'preview_invoice_reminder'
            ? 'preview'
            : null;
  if (invoiceReminderAction) {
    let reminderQuery = supabase.rpc('invoice_reminder_rpc', {
      p_action: invoiceReminderAction,
      p_args: args,
    });
    if (signal) reminderQuery = reminderQuery.abortSignal(signal);
    const { data, error } = await reminderQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Invoice reminder command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  const billingReviewAction =
    tool === 'get_unbilled_work_review'
      ? 'detail'
      : tool === 'list_unbilled_work_attention'
        ? 'attention'
        : tool === 'get_unbilled_work_settings'
          ? 'settings'
          : null;
  if (billingReviewAction) {
    let billingQuery = supabase.rpc('billing_review_rpc', {
      p_action: billingReviewAction,
      p_args: args,
    });
    if (signal) billingQuery = billingQuery.abortSignal(signal);
    const { data, error } = await billingQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Direct billing-review command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  const operatorDeskAction =
    tool === 'list_operator_handoffs'
      ? 'list'
      : tool === 'get_operator_handoff'
        ? 'detail'
        : tool === 'set_operator_handoff_snooze'
          ? 'snooze'
          : tool === 'get_operator_desk_settings'
            ? 'settings'
            : null;
  if (operatorDeskAction) {
    let operatorQuery = supabase.rpc('operator_desk_rpc', {
      p_action: operatorDeskAction,
      p_args: args,
    });
    if (signal) operatorQuery = operatorQuery.abortSignal(signal);
    const { data, error } = await operatorQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Direct Operator Desk command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  if (tool === 'list_bank_connections') {
    let bankQuery = supabase.rpc('plaid_connection_rpc', { p_action: 'status', p_args: args });
    if (signal) bankQuery = bankQuery.abortSignal(signal);
    const { data, error } = await bankQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Direct bank connection command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  const billAction =
    tool === 'list_bills'
      ? 'list'
      : tool === 'get_bill'
        ? 'get'
        : tool === 'get_bill_settings'
          ? 'settings'
          : tool === 'create_bill'
            ? 'create'
            : tool === 'update_bill'
              ? 'update'
              : null;
  if (billAction) {
    let billQuery = supabase.rpc('bill_rpc', { p_action: billAction, p_args: args });
    if (signal) billQuery = billQuery.abortSignal(signal);
    const { data, error } = await billQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Direct bill command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  const customerHubAction =
    tool === 'get_customer_hub'
      ? 'status'
      : tool === 'issue_customer_hub'
        ? 'issue'
        : tool === 'revoke_customer_hub'
          ? 'revoke'
          : tool === 'resolve_customer_service_request'
            ? 'resolve_request'
            : tool === 'get_customer_hub_settings'
              ? 'settings'
              : null;
  if (customerHubAction) {
    let hubQuery = supabase.rpc('customer_hub_rpc', {
      p_action: customerHubAction,
      p_args: args,
    });
    if (signal) hubQuery = hubQuery.abortSignal(signal);
    const { data, error } = await hubQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Direct Customer Hub command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  const servicePlanAction =
    tool === 'list_service_plans'
      ? 'list'
      : tool === 'get_service_plan'
        ? 'detail'
        : tool === 'preview_service_plan'
          ? 'preview'
          : null;
  if (servicePlanAction) {
    let planQuery = supabase.rpc('service_plan_rpc', {
      p_action: servicePlanAction,
      p_args: args,
    });
    if (signal) planQuery = planQuery.abortSignal(signal);
    const { data, error } = await planQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Service plan command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  const intakeAction =
    tool === 'list_intake_forms'
      ? 'forms'
      : tool === 'list_intake_submissions'
        ? 'submissions'
        : tool === 'get_intake_submission'
          ? 'submission'
          : null;
  if (intakeAction) {
    let intakeQuery = supabase.rpc('intake_rpc', {
      p_action: intakeAction,
      p_args: args,
    });
    if (signal) intakeQuery = intakeQuery.abortSignal(signal);
    const { data, error } = await intakeQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Intake command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  const leadResponseAction =
    tool === 'list_lead_response_attention'
      ? 'attention'
      : tool === 'get_lead_response'
        ? 'detail'
        : tool === 'get_lead_response_settings'
          ? 'settings'
          : null;
  if (leadResponseAction) {
    let leadQuery = supabase.rpc('lead_response_rpc', {
      p_action: leadResponseAction,
      p_args: args,
    });
    if (signal) leadQuery = leadQuery.abortSignal(signal);
    const { data, error } = await leadQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Lead response command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  const leadResponseCommand =
    tool === 'record_lead_contact'
      ? 'record_contact'
      : tool === 'schedule_lead_follow_up'
        ? 'schedule_follow_up'
        : null;
  if (leadResponseCommand) {
    let leadQuery = supabase.rpc('lead_response_command_rpc', {
      p_action: leadResponseCommand,
      p_args: args,
    });
    if (signal) leadQuery = leadQuery.abortSignal(signal);
    const { data, error } = await leadQuery;
    if (error) {
      if (directUnavailable(error.code)) return null;
      throw new RpcError(directStatus(error.code), {
        error: error.code || 'internal',
        message: error.message,
        hint: error.hint || undefined,
        details: error.details ? { details: error.details } : undefined,
      });
    }
    const envelope = data as DirectEnvelope<T> | null;
    if (!envelope?.data)
      throw new RpcError(500, {
        error: 'internal',
        message: 'Lead response command returned no data.',
      });
    return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
  }

  let query = supabase.rpc('admin_rpc', { p_tool: tool, p_args: args });
  if (signal) query = query.abortSignal(signal);
  const { data, error } = await query;
  if (error) {
    if (directUnavailable(error.code)) return null;
    throw new RpcError(directStatus(error.code), {
      error: error.code || 'internal',
      message: error.message,
      hint: error.hint || undefined,
      details: error.details ? { details: error.details } : undefined,
    });
  }
  const envelope = data as DirectEnvelope<T> | null;
  if (!envelope || !('data' in envelope)) {
    throw new RpcError(500, {
      error: 'internal',
      message: `Direct database command "${tool}" returned no data.`,
    });
  }
  return { ok: true, summary: envelope.summary ?? '', data: envelope.data };
}

/** Call a tool. Returns the full envelope so callers can read preview tokens. */
export async function callTool<T = unknown>(
  tool: string,
  args: Record<string, unknown> = {},
  opts: CallOptions = {},
): Promise<RpcOk<T>> {
  // Dry-run and confirmation semantics are still owned by the Worker. Normal
  // human UI mutations are already explicit and can use the database command.
  if (
    (DIRECT_ADMIN_TOOLS.has(tool) || tool === 'evaluate_lifecycle_transition') &&
    !opts.dryRun &&
    !opts.confirmToken
  ) {
    const direct = await callDirect<T>(tool, args, opts.signal);
    if (direct) return direct;
  }
  return request<RpcOk<T>>(`/rpc/${encodeURIComponent(tool)}`, {
    method: 'POST',
    body: JSON.stringify({
      args,
      ...(opts.dryRun ? { dry_run: true } : {}),
      ...(opts.confirmToken ? { confirm: { token: opts.confirmToken } } : {}),
    }),
    signal: opts.signal,
  });
}

/** Call a tool and return just its data. */
export async function toolData<T = unknown>(
  tool: string,
  args: Record<string, unknown> = {},
  opts: CallOptions = {},
): Promise<T> {
  return (await callTool<T>(tool, args, opts)).data;
}

/**
 * Run a confirmation-gated tool in one step, passing the token straight back.
 *
 * Only for actions where the user has already seen exactly what will happen and
 * pressed a button to do it — saving a form they just filled in, or choosing a
 * starting preset. Anything with consequences the user cannot already see must
 * use the two-step flow in `gated.tsx` so the preview is shown first.
 */
export async function callWithAutoConfirm<T = unknown>(
  tool: string,
  args: Record<string, unknown> = {},
): Promise<RpcOk<T>> {
  const first = await callTool<T>(tool, args);
  if (!first.confirmation_token) return first;
  return callTool<T>(tool, args, { confirmToken: first.confirmation_token });
}

export function fetchTools(signal?: AbortSignal): Promise<RpcToolsResponse> {
  return request<RpcToolsResponse>(RPC_TOOLS_PATH, { method: 'GET', signal });
}

/** Authenticated call to a non-tool route on the API (e.g. Gmail OAuth helpers). */
export function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  return request<T>(path, init);
}
